import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { deviceDetection, performance, mobileOptimizations } from '../utils/mobileUtils';

interface ScrollRevealSettings {
  threshold: number[];
  rootMargin: string;
  enableAnimations: boolean;
  reducedMotion: boolean;
  staggerDelay: number;
  animationDuration: number;
}

interface RevealElement {
  id: string;
  element: HTMLElement;
  callback: (entry: IntersectionObserverEntry) => void;
  options?: {
    threshold?: number[];
    rootMargin?: string;
    triggerOnce?: boolean;
    delay?: number;
  };
  triggered?: boolean;
}

interface ScrollRevealContextType {
  settings: ScrollRevealSettings;
  registerElement: (element: RevealElement) => () => void;
  updateSettings: (newSettings: Partial<ScrollRevealSettings>) => void;
  scrollY: number;
  isScrolling: boolean;
  scrollDirection: 'up' | 'down' | null;
}

const ScrollRevealContext = createContext<ScrollRevealContextType | null>(null);

interface ScrollRevealProviderProps {
  children: ReactNode;
  customSettings?: Partial<ScrollRevealSettings>;
}

export function ScrollRevealProvider({ 
  children, 
  customSettings = {} 
}: ScrollRevealProviderProps) {
  const [settings, setSettings] = useState<ScrollRevealSettings>({
    threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
    rootMargin: '0px 0px -50px 0px',
    enableAnimations: true,
    reducedMotion: false,
    staggerDelay: 100,
    animationDuration: 600,
    ...customSettings
  });

  const [scrollY, setScrollY] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | null>(null);
  
  const elementsRef = useRef<Map<string, RevealElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const scrollTimeoutRef = useRef<number>();
  const lastScrollYRef = useRef(0);
  const rafRef = useRef<number>();

  // Initialize mobile-optimized settings
  useEffect(() => {
    const initializeSettings = async () => {
      const mobileSettings = await mobileOptimizations.getRecommendedSettings();
      const prefersReduced = deviceDetection.prefersReducedMotion();
      const isLowEnd = performance.isLowEndDevice();

      setSettings(current => ({
        ...current,
        enableAnimations: mobileSettings.enableAnimations && !prefersReduced,
        reducedMotion: prefersReduced || isLowEnd,
        animationDuration: mobileSettings.animationDuration * 1000, // Convert to ms
        staggerDelay: isLowEnd ? 50 : 100,
        threshold: isLowEnd 
          ? [0, 0.25, 0.5] 
          : [0, 0.1, 0.25, 0.5, 0.75, 1],
        rootMargin: deviceDetection.isMobile() ? '0px 0px -30px 0px' : '0px 0px -50px 0px'
      }));
    };

    initializeSettings();
  }, []);

  // Set up Intersection Observer
  useEffect(() => {
    if (!settings.enableAnimations) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const element = elementsRef.current.get(entry.target.id);
          if (!element) return;

          // Skip if already triggered and triggerOnce is enabled
          if (element.triggered && element.options?.triggerOnce) return;

          element.callback(entry);

          if (entry.isIntersecting && element.options?.triggerOnce) {
            element.triggered = true;
          }
        });
      },
      {
        threshold: settings.threshold,
        rootMargin: settings.rootMargin
      }
    );

    // Observe all registered elements
    elementsRef.current.forEach((element) => {
      observerRef.current?.observe(element.element);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [settings.enableAnimations, settings.threshold, settings.rootMargin]);

  // Scroll tracking with RAF optimization
  useEffect(() => {
    let ticking = false;

    const updateScrollState = () => {
      const currentScrollY = window.scrollY;
      const direction = currentScrollY > lastScrollYRef.current ? 'down' : 'up';
      
      setScrollY(currentScrollY);
      setScrollDirection(direction);
      setIsScrolling(true);
      
      lastScrollYRef.current = currentScrollY;
      ticking = false;

      // Clear scrolling state after delay
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = window.setTimeout(() => {
        setIsScrolling(false);
        setScrollDirection(null);
      }, 150);
    };

    const handleScroll = () => {
      if (!ticking) {
        rafRef.current = requestAnimationFrame(updateScrollState);
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  const registerElement = (element: RevealElement): (() => void) => {
    // Generate unique ID if not provided
    if (!element.id) {
      element.id = `reveal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    elementsRef.current.set(element.id, element);
    
    // Observe with current observer if available
    if (observerRef.current) {
      observerRef.current.observe(element.element);
    }

    // Return cleanup function
    return () => {
      const elementData = elementsRef.current.get(element.id);
      if (elementData && observerRef.current) {
        observerRef.current.unobserve(elementData.element);
      }
      elementsRef.current.delete(element.id);
    };
  };

  const updateSettings = (newSettings: Partial<ScrollRevealSettings>) => {
    setSettings(current => ({ ...current, ...newSettings }));
  };

  const value: ScrollRevealContextType = {
    settings,
    registerElement,
    updateSettings,
    scrollY,
    isScrolling,
    scrollDirection
  };

  return (
    <ScrollRevealContext.Provider value={value}>
      {children}
    </ScrollRevealContext.Provider>
  );
}

export function useScrollReveal(): ScrollRevealContextType {
  const context = useContext(ScrollRevealContext);
  if (!context) {
    throw new Error('useScrollReveal must be used within a ScrollRevealProvider');
  }
  return context;
}

// Higher-order component for easy wrapping
export function withScrollReveal<P extends object>(
  Component: React.ComponentType<P>,
  settings?: Partial<ScrollRevealSettings>
) {
  return function ScrollRevealWrapper(props: P) {
    return (
      <ScrollRevealProvider customSettings={settings}>
        <Component {...props} />
      </ScrollRevealProvider>
    );
  };
}